import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { UserService } from '../UserService';
import { IAdvancedSocialPlatform } from '../../../model/user/IAdvancedSocialPlatform';
import ToggleChip from '../../../shared/components/ToggleChip';
import generalSlice from '../../../store/general/GeneralSlice';
import { ManualSocialModal } from './ManualSocialModal';
import { BlockingOverlay } from '../../../shared/components/BlockingOverlay';
import { formatDate } from '../../../utils/Functions';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';

interface IUserAdvancedSocialTab {
    userId: number;
    isActive: boolean;
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
    approved: { label: 'Connected', className: 'bg-success-subtle text-success' },
    pending: { label: 'Pending verification', className: 'bg-warning-subtle text-warning' },
    rejected: { label: 'Rejected', className: 'bg-danger-subtle text-danger' },
};

export function UserAdvancedSocialTab({ userId, isActive }: IUserAdvancedSocialTab) {
    const dispatch = useDispatch();
    const [platforms, setPlatforms] = useState<IAdvancedSocialPlatform[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [handles, setHandles] = useState<Record<number, string>>({});
    const [busy, setBusy] = useState<string | null>(null);
    const [busyMessage, setBusyMessage] = useState('');
    const [manualPlatform, setManualPlatform] = useState<IAdvancedSocialPlatform | null>(null);

    const load = useCallback(() => {
        setIsLoading(true);
        return UserService.getAdvancedSocial(userId)
            .then(data => {
                setPlatforms(data);
                setHasLoaded(true);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsLoading(false));
    }, [userId]);

    useEffect(() => {
        if (isActive && !hasLoaded) {
            load();
        }
    }, [isActive, hasLoaded, load]);

    const confirmAction = (title: string, content: string, action: () => void) =>
        dispatch(
            generalSlice.actions.setConfirmModal({
                show: true,
                title,
                contentBody: content,
                action: () => {
                    dispatch(generalSlice.actions.setConfirmModal({ show: false }));
                    action();
                },
            })
        );

    const run = (key: string, request: () => Promise<unknown>, successMessage: string, message: string) => {
        setBusy(key);
        setBusyMessage(message);
        request()
            .then(() => {
                SuccessToast(successMessage);
                return load();
            })
            .catch(err => ErrorToast(err))
            .finally(() => setBusy(null));
    };

    const connect = (platform: IAdvancedSocialPlatform) => {
        const handle = (handles[platform.id] ?? '').trim().replace(/^@/, '');
        if (!handle) return;
        confirmAction('Connect handle?', `@${handle} on ${platform.name}`, () =>
            run(
                `${platform.id}:connect`,
                () => UserService.connectAdvancedSocial(userId, platform.id, handle),
                'Platform connected',
                `Connecting @${handle} on ${platform.name}...`
            )
        );
    };

    const disconnect = (platform: IAdvancedSocialPlatform) =>
        confirmAction(
            `Disconnect ${platform.name}?`,
            platform.hasOffers
                ? 'This influencer has ongoing offers. Disconnecting removes the platform and its audience data.'
                : 'This removes the platform and its audience data from the influencer.',
            () =>
                run(
                    `${platform.id}:disconnect`,
                    () => UserService.disconnectAdvancedSocial(userId, platform.id),
                    'Platform disconnected',
                    `Disconnecting ${platform.name}...`
                )
        );

    if (isLoading && !hasLoaded) {
        return (
            <div className="text-center py-10">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <>
            <BlockingOverlay show={busy !== null} message={busyMessage} />
            <ManualSocialModal
                show={manualPlatform !== null}
                userId={userId}
                platform={manualPlatform}
                onHide={() => setManualPlatform(null)}
                onSaved={() => {
                    setManualPlatform(null);
                    load();
                }}
            />
            <Row className="g-4 mb-4">
                {platforms.map(platform => {
                    const isConnected = !!platform.username;
                    const badge = isConnected
                        ? STATUS_BADGES[platform.status ?? 'approved'] ?? STATUS_BADGES.approved
                        : { label: 'Not connected', className: 'bg-secondary-subtle text-secondary' };
                    const isBusy = busy !== null;

                    return (
                        <Col key={platform.id} md={6} xl={4}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            {platform.path && (
                                                <img src={platform.path} alt={platform.name} width={33} />
                                            )}
                                            <div>
                                                <div className="fw-bold">{platform.name}</div>
                                                <div className="text-muted fs-7">
                                                    {platform.username ? `@${platform.username}` : 'n.a.'}
                                                </div>
                                            </div>
                                        </div>
                                        <Badge bg="" className={`fs-8 ${badge.className}`}>
                                            {badge.label}
                                        </Badge>
                                    </div>

                                    {isConnected && (
                                        <div className="text-muted fs-7">
                                            {platform.audienceUpdate
                                                ? `Last update ${formatDate(platform.audienceUpdate)}`
                                                : 'No audience update yet'}
                                            {platform.manualUserInput ? ' · Manual data' : ''}
                                        </div>
                                    )}

                                    {isConnected && (
                                        <div>
                                            <ToggleChip
                                                name={`platform-${platform.id}`}
                                                label="Enabled"
                                                checked={!!platform.isEnabled}
                                                disabled={isBusy}
                                                onChange={() =>
                                                    run(
                                                        `${platform.id}:toggle`,
                                                        () => UserService.toggleAdvancedSocial(userId, platform.id),
                                                        platform.isEnabled ? 'Platform disabled' : 'Platform enabled',
                                                        `${platform.isEnabled ? 'Disabling' : 'Enabling'} ${
                                                            platform.name
                                                        }...`
                                                    )
                                                }
                                            />
                                        </div>
                                    )}

                                    {!isConnected && platform.hasApi && (
                                        <InputGroup>
                                            <InputGroup.Text>@</InputGroup.Text>
                                            <Form.Control
                                                placeholder="Handle"
                                                value={handles[platform.id] ?? ''}
                                                onChange={event =>
                                                    setHandles(prev => ({
                                                        ...prev,
                                                        [platform.id]: event.target.value.replace(/\s/g, ''),
                                                    }))
                                                }
                                                disabled={isBusy}
                                            />
                                            <Button
                                                onClick={() => connect(platform)}
                                                disabled={isBusy || !(handles[platform.id] ?? '').trim()}
                                            >
                                                {busy === `${platform.id}:connect` ? 'Connecting...' : 'Connect'}
                                            </Button>
                                        </InputGroup>
                                    )}

                                    {(!isConnected && !platform.hasApi) || platform.status === 'rejected' ? (
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                size="sm"
                                                variant={platform.status === 'rejected' ? 'outline-danger' : 'primary'}
                                                disabled={isBusy}
                                                onClick={() => setManualPlatform(platform)}
                                            >
                                                Add manually
                                            </Button>
                                        </div>
                                    ) : null}

                                    {isConnected && (
                                        <div className="d-flex gap-2 mt-auto">
                                            {platform.hasApi && !platform.manualUserInput && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    disabled={isBusy}
                                                    onClick={() =>
                                                        run(
                                                            `${platform.id}:refresh`,
                                                            () =>
                                                                UserService.refreshAdvancedSocial(userId, platform.id),
                                                            'Data refreshed',
                                                            `Refreshing ${platform.name} data...`
                                                        )
                                                    }
                                                >
                                                    {busy === `${platform.id}:refresh`
                                                        ? 'Refreshing...'
                                                        : 'Refresh data'}
                                                </Button>
                                            )}
                                            {platform.status === 'approved' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    disabled={isBusy}
                                                    onClick={() => setManualPlatform(platform)}
                                                >
                                                    Manual update
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                disabled={isBusy}
                                                onClick={() => disconnect(platform)}
                                            >
                                                Disconnect
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
}
